'use client';

import { Tabs } from '@mv/web-ui';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type ComponentProps, type JSX, useMemo } from 'react';
import { useDataTagsFactory } from '@/modules/tracking/analytics/useDataTags';

type TabItem = {
	id: string | number;
	label: string;
	content?: JSX.Element;
	slug: string;
};

type TabsGroupClientProps = Omit<ComponentProps<typeof Tabs.Root>, 'children' | 'defaultValue' | 'onValueChange'> & {
	tabs?: TabItem[];
};

export default function TabsGroup({ tabs, ...rootProps }: TabsGroupClientProps) {
	const { createDataTags } = useDataTagsFactory();
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const value = useMemo(() => {
		if (!tabs?.length) return undefined;

		const param = searchParams?.get('tab');
		if (param && tabs.some((t) => t.slug === param)) {
			return param;
		}

		return tabs[0]?.slug;
	}, [tabs, searchParams]);

	if (!tabs?.length || !value) return null;

	const onChangeTab = (newValue: string) => {
		const params = new URLSearchParams(searchParams?.toString());
		params.set('tab', newValue);

		router.replace(`${pathname}?${params.toString()}`, { scroll: false });
	};

	return (
		<Tabs.Root {...rootProps} defaultValue={value} onValueChange={onChangeTab}>
			<Tabs.List>
				{tabs.map((tab) => {
					const { blockRef, labelRef } = createDataTags('tab', tab.id);

					return (
						<Tabs.Trigger key={tab.id} ref={blockRef} value={tab.slug}>
							<span ref={labelRef}>{tab.label}</span>
						</Tabs.Trigger>
					);
				})}
			</Tabs.List>
			{tabs.map((tab) => {
				const { contentRef } = createDataTags('tab', tab.id);

				return (
					<Tabs.Content key={tab.id} value={tab.slug} ref={contentRef}>
						{tab.content}
					</Tabs.Content>
				);
			})}
		</Tabs.Root>
	);
}
